<template>
  <div class="w-full flex flex-row justify-center font-rubik">
    <div class="w-full flex flex-col justify-center items-center max-w-1216 py-12 find-inventory-bg-color">
      <div class="w-full flex flex-col justify-center items-center" style="z-index: 1;">
        <div class="find-inventory-container w-full max-w-1012 flex flex-col 1216-bp:flex-row">
          <div class="w-full flex flex-col 1216-bp:justify-end gap-4 1216-bp:gap-2 h-2/6 1216-bp:h-auto find-inventory-input-container">
            <div class="flex justify-center 1216-bp:justify-start">
              <div class="find-inventory-h2-container">
                <h2 class="find-inventory-h2 font-rubik">Find an inventory</h2>
              </div>
            </div>
            <div>
              <div class="flex flex-col 1216-bp:flex-row justify-between items-center gap-2 1216-bp:gap-1">
                <div class="input-container relative flex flex-row justify-around items-center">
                  <div class="flex items-center gap-1 geo-location-container">
                    <geolocate @success="searchByCoordinates" :showIcon="false" :onlyIcon="true" />
                    <img v-if="loading" class="animate-spin h-3" src="@/images/spinner.svg" alt="Loading">
                  </div>
                  <input
                    @keyup.enter="search"
                    @click="coordinates = null"
                    v-model="query"
                    :class="{ 'is-danger': error }"
                    class="bg-white w-full focus:ring-0 focus:outline-none pl-3"
                    type="search"
                    placeholder="Where are you located?"
                  />
                  <button @click="search" class="flex justify-center items-center">
                    <img class="h-7" src="@/images/find-inventory/search.svg" alt="Search">
                  </button>
                  <div
                    v-if="cities.length && query.length > 0"
                    role="menu"
                    class="dropdown-content absolute"
                  >
                    <p class="dropdown-item"><b>We found multiple results...</b></p>
                    <a v-for="city in cities" :key="city.id" :href="cityPath(city.node)" class="dropdown-item">
                      {{ city.node.name }}, {{ city.node.stateId | upcase }}
                    </a>
                  </div>
                  <div v-if="error" class="error-message absolute">
                    {{ error }}
                    <b><a @click.prevent="resetSearch()">Try Again</a></b>
                  </div>
                </div>
                <toggle class=" mr-10 pr-4" @toggleValue="setToggleOption" />
              </div>
            </div>
          </div>
          <div class="hidden 1216-bp:block w-full find-inventory-map-container">
            <img class="usa-map" src="@/images/find-inventory/us-map.svg" alt="USA Map" />
          </div>
          <div class="1216-bp:hidden w-full flex justify-center items-center h-4/6 find-inventory-map-container">
            <img class="usa-map-mobile" src="@/images/find-inventory/us-map.svg" alt="USA Map" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ahoy from 'shared/ahoy'
import Geolocate from '../location-search/geolocate.vue'
import Toggle from './helpers/toggle.vue'
import { zipQuery, citySearchQuery, geoSearchQuery, solutionByCity } from 'shared/graphql/queries'
import { graphqlQuery } from 'shared/graphql'
import { cityPath } from '../../shared/city-helper'
import { mapState } from 'vuex'

const ZIP_REGEX = /^\d{5}$/
const redirectToCity = (city) => (window.location.href = cityPath(city))

export default {
  components: {
    Geolocate,
    Toggle
  },
  data() {
    return {
      loading: 0,
      solutionSlug: '',
      error: null,
      query: '',
      coordinates: null,
      cities: []
    }
  },
  filters: {
    upcase(string) {
      return string.toUpperCase()
    }
  },
  computed: {
    ...mapState('shared', ['zipcode']),
    ...mapState('quote', ['lead'])
  },
  watch: {
    query(newValue) {
      if (newValue.length == 0) {
        this.cities = []
      }
    }
  },
  methods: {
    cityPath,
    handleClose() {
      this.$el.parentNode.removeChild(this.$el)
      this.$emit('close')
    },
    searchByCoordinates(coordinates) {
      this.coordinates = coordinates
      this.query = ''
      this.search()
    },
    resetSearch() {
      this.query = ''
      this.error = null
    },
    getRequestBody() {
      if (ZIP_REGEX.test(this.query)) {
        return {
          query: zipQuery,
          variables: {
            id: this.query
          }
        }
      } else if (this.coordinates) {
        return {
          query: geoSearchQuery,
          variables: {
            coordinates: this.coordinates
          }
        }
      } else {
        return {
          query: citySearchQuery,
          variables: {
            query: this.query
          }
        }
      }
    },
    async trackSearch() {
      ahoy.track('location.search', {
        coordinates: this.coordinates,
        query: this.query
      })
    },
    async search() {
      this.cities = []
      this.loading++
      this.lastQuery = this.query = this.query.trim()
      try {
        this.trackSearch()
        const result = await graphqlQuery(this.getRequestBody())
        if (result.data.zipcode) {
          const aboutSlug = `/service-areas/${result.data.zipcode.city.id}/`
          localStorage.setItem('serviceAreaAboutSlug', JSON.stringify(aboutSlug)) 
        } 
        return this.handleResult(result.data || {})
      } catch (e) {
        console.warn(e)
        this.error = 'There was an error while searching.'
        this.loading = 0
      }
    },
    async handleResult(result) {
      let { zipcode, zipcodes, cities } = result
      if (!zipcode && zipcodes && zipcodes.edges.length === 1) {
        zipcode = zipcodes.edges[0].node
      }
      if (this.solutionSlug && zipcode) {
        const data = await this.getSolutionPage(zipcode)
        if (data && data.solutionByCity && data.solutionByCity.solution) {
          if (this.lead) {
            await this.$store.dispatch('quote/lead.zipcode.update', {
              lead: this.lead,
              zipcode: zipcode.id
            })
          } else {
            this.$store.commit('shared/zipcode.set', zipcode.id)
          }
          this.$emit('close')
          this.loading = 0
          window.location.href = `/partners/${data.solutionByCity.organization.id}/${data.solutionByCity.solution.slug}`
          return
        }
      }
      if (zipcode) {
        if (this.lead) {
          await this.$store.dispatch('quote/lead.zipcode.update', { lead: this.lead, zipcode: zipcode.id })
        } else {
          this.$store.commit('shared/zipcode.set', zipcode.id)
        }
        this.$emit('close')
        return redirectToCity(zipcode.city)
      } else if (cities && cities.edges.length) {
        if (cities.edges.length === 1) {
          this.$emit('close')
          return redirectToCity(cities.edges[0].node)
        } else {
          this.cities = cities.edges
        }
      } else {
        this.error = 'Sorry, we couldn\'t find that location.'
      }
      this.loading = 0
    },
    async getSolutionPage(zipcode) {
      const body = {
        query: solutionByCity,
        variables: {
          cityId: zipcode.city.id,
          solutionSlug: this.solutionSlug
        }
      }
      const result = await graphqlQuery(body)
      return result.data
    },
    setToggleOption(value) {
      this.solutionSlug = value
    }
  }
}
</script>

<style scoped>
.find-inventory-container {
  z-index: 10;
  height: 405px;
  background: linear-gradient(0deg, #e3e2df, #e3e2df),
    linear-gradient(270deg, rgba(226, 225, 222, 0) 0%, #e2e1de 40.91%);
  border: 1px solid #c7c2bb;
  border-radius: 16px;
  box-shadow: 13px 7px 33px 0px #4d83ff1f, 54px 27px 60px 0px #4d83ff1a,
    120px 60px 81px 0px #4d83ff0f, 214px 106px 96px 0px #4d83ff05,
    -18px 16px 53px 0px #c6e2e51f, -71px 64px 96px 0px #c6e2e51a,
    -160px 145px 130px 0px #c6e2e50f;
}

.find-inventory-map-container {
  position: relative;
  overflow: visible;
}

.find-inventory-map-container::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 66.66%;
  height: 100%;
  background-image: linear-gradient(to right, rgba(227, 226, 223, 1), rgba(227, 226, 223, 0)),
    url('@/images/find-inventory/bg01.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.5;
  border-radius: 12px
}

.usa-map {
  position: absolute;
  top: 50%;
  right: -15%;
  transform: translateY(-50%);
  width: 110%;
  max-width: none;
  z-index: 1;
}

.find-inventory-h2 {
  font-size: 40px;
  font-weight: 400;
  line-height: 48px;
  letter-spacing: -0.04em;
  color: black;
}

.geo-location-container {
  border-right: 1px solid #c7c2bb;
  padding-right: 8px;
  height: auto;
}

.input-container {
  position: relative;
  width: 310px;
  height: 40px;
  padding: 0px 10px;
  border-radius: 8px;
  border: 1px solid #c7c2bb;
  background-color: #ffffff;
}

.dropdown-content {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  width: calc(100% - 0px);
  z-index: 1000;
  background-color: white;
  border: 1px solid #c7c2bb;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0.5rem;
}

.error-message {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: white;
  border: 1px solid #c7c2bb;
  border-radius: 8px;
  padding: 2px 10px;
  text-align: center;
  color: #d9534f;
  font-size: 11px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.usa-map-mobile {
  width: 100%;
  max-width: 310px;
  height: 100%;
  object-fit: contain;
  z-index: 1;
  
}

.find-inventory-input-container {
  padding-left: 24px;
  padding-bottom: 24px;  
}

.find-inventory-bg-color {
  position: relative;
  background: #ffffff;
}

.find-inventory-bg-color::before {
  content: '';
  position: absolute;
  top: 33%;
  left: 0;
  right: 0;
  bottom: 0;
  background: #334155;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  z-index: 1;
}

/* mobile */
@media(max-width: 1012px) {
  .find-inventory-h2-container {
    width: 310px;
  }

  .find-inventory-h2 {
    font-size: 32px;
    font-weight: 400;
    line-height: 38.4px;
    letter-spacing: -0.04em;
    color: black;
    text-align: left;
  }
  .find-inventory-container { 
    max-width: 89%;
  }
  .find-inventory-bg-color::before {    
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
    background: #0C0E20;
  }
}

@media(max-width: 1216px) {
  .find-inventory-input-container {
    padding-left: 0px;
    padding-bottom: 0px;
    padding: 16px;
  }

  .find-inventory-map-container {
    position: relative;
    overflow: visible;
  }

  .find-inventory-map-container::before {
    content: '';
    position: absolute;
    bottom: 0;
    top: auto;
    width: 100%;
    height: 80%;
    background-image: linear-gradient(to bottom, rgba(227, 226, 223, 1), rgba(227, 226, 223, 0)),
      url('@/images/find-inventory/bg01.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.4;
    border-radius: 12px;
  }

  .find-inventory-bg-color::before {
    top: 50%;
    height: 50%;
  }
}
</style>
